// src/components/ResultsPanel.jsx
import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ListView from "./listview";
import BarChartCard from "./Charts/BarChartCard";

const panelVariants = {
  hidden: { opacity: 0, y: 10, scale: 0.99 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.25 } },
  exit: { opacity: 0, y: 10, scale: 0.99, transition: { duration: 0.18 } },
};

export default function ResultsPanel({ show, onClose, results, companyName }) {
  const rankingStats = useMemo(() => {
    if (!Array.isArray(results) || !companyName) {
      return {
        first: 0,
        top3: 0,
        others: 0,
        total: 0,
        noOffer: results?.length || 0,
      };
    }

    const normalizedName = companyName.trim().toLowerCase();
    if (!normalizedName) {
      return {
        first: 0,
        top3: 0,
        others: 0,
        total: 0,
        noOffer: results?.length || 0,
      };
    }

    let first = 0;
    let top3 = 0;
    let others = 0;
    let total = 0;
    let noOffer = 0;

    results.forEach((item) => {
      if (!item) return;

      const allOffers = [];
      if (item.main_offer) allOffers.push(item.main_offer);
      if (Array.isArray(item.other)) allOffers.push(...item.other);

      if (!allOffers.length) {
        noOffer++;
        return;
      }

      const sorted = [...allOffers].sort((a, b) => {
        const pa =
          typeof a.Sale_Price === "number" ? a.Sale_Price : Number.MAX_VALUE;
        const pb =
          typeof b.Sale_Price === "number" ? b.Sale_Price : Number.MAX_VALUE;
        return pa - pb;
      });

      const clientIndex = sorted.findIndex((offer) => {
        if (!offer || !offer.Seller) return false;
        const seller = offer.Seller.trim().toLowerCase();
        return seller.includes(normalizedName);
      });

      if (clientIndex === -1) {
        noOffer++;
        return;
      }

      const position = clientIndex + 1;
      total++;

      if (position === 1) first++;
      else if (position <= 3) top3++;
      else others++;
    });

    return { first, top3, others, total, noOffer };
  }, [results, companyName]);

  const { first, top3, others, total, noOffer } = rankingStats;

  const chartData = [
    { name: "Locul 1", value: first },
    { name: "Top 3", value: top3 },
    { name: "După locul 3", value: others },
    { name: "Fără ofertă", value: noOffer },
  ];

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          variants={panelVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginTop: "6rem",
          }}
        >
          <div
            style={{
              background: "#222161ff",
              borderRadius: "1rem",
              width: "min(90vw)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              padding: "1rem",
              boxShadow: "0 10px 20px rgba(0,0,0,0.25)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "1rem",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "1.5rem",
                    color: "white",
                  }}
                >
                  Rezultate procesate
                </h3>

                <div style={{ width: "100%" }}>
                  <BarChartCard
                    data={chartData}
                    title={
                      companyName
                        ? `Poziția ofertelor tale (pentru ${total} produse)`
                        : "Poziția ofertelor tale"
                    }
                    xKey="name"
                    yKey="value"
                    height={220}
                    style={{ width: "100%" }}
                  />
                </div>
              </div>

              <button
                onClick={onClose}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "white",
                  fontSize: "1.2rem",
                  cursor: "pointer",
                  lineHeight: 1,
                }}
                aria-label="Închide"
              >
                ✖
              </button>
            </div>

            {/* List */}
            <div style={{ overflowY: "auto", marginTop: "1rem" }}>
              <ListView items={results} sellerName={companyName} />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
